import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "intelligent-diagnostic-systems",
  kind: "horizontal",
  title: "Intelligent Visual & Diagnostic Systems",
  shortTitle: "Intelligent Diagnostics",
  status: "draft",
  tier: "exploring",
  seoTitle: "Intelligent Visual & Diagnostic Systems | GA",
  seoDescription:
    "Computer vision, thermal imaging, and AI diagnostic pipelines for automated crop health scouting, solar array inspection, and asset condition monitoring.",
  summary:
    "Computer vision and multispectral diagnostic pipelines that automate physical inspections: crop disease scouting, solar panel hotspot detection, and equipment thermal analysis.",
  image: "/images/blog/interactive-industrial-schematics-hero.webp",
  relatedCapabilitySlugs: ["intelligent-systems", "automation-operations"],
  relatedIndustrySlugs: ["agriculture", "energy", "enterprise", "military"],
  relatedPostSlugs: [
    "deterministic-controls-ai-agents",
    "evaluating-2d-and-3d-web-graphics",
    "clinical-workflow-friction-alert-fatigue",
  ],
  engineering: [
    "Multispectral, RGB, and thermal computer vision model fine-tuning for physical asset defect identification",
    "Automated crop canopy stress, pest pathogen detection, and weed pressure spatial segmentation",
    "Solar photovoltaic string defect, hotspot, and transformer thermal gradient analysis pipelines",
    "Edge-optimized inference models for offline drone, tractor-mounted, and handheld diagnostic capture",
    "Georeferenced bounding box annotations tied directly to GIS shapefiles and asset inventory registries",
    "Diagnostic triage consoles with automated severity scoring, historical comparisons, and work order creation",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Manual Physical Inspection Does Not Scale",
    },
    {
      type: "paragraph",
      text: "Whether walking thousands of crop acres to detect early fungal outbreaks or inspecting utility-scale solar farms and transmission corridors for cracked panels and overheating switchgear, manual physical inspection is painfully slow and geographically limited. Critical defects are routinely discovered only after significant yield loss or asset failure has occurred.",
    },
    {
      type: "heading",
      level: 2,
      text: "Domain-Specific Visual and Multispectral AI",
    },
    {
      type: "paragraph",
      text: "Off-the-shelf vision models fail when applied to specialized field environments. They confuse shadow artifacts with plant disease and fail to interpret thermal radiometric metadata. We engineer specialized diagnostic vision pipelines trained on radiometric infrared, NDVI multispectral bands, and high-resolution spatial imagery, delivering precise defect classification and severity scoring.",
    },
    {
      type: "heading",
      level: 2,
      text: "Edge Inference and GIS Integration",
    },
    {
      type: "paragraph",
      text: "Inspections frequently happen in remote fields or substations with zero cellular connectivity. Our vision pipelines run lightweight, quantized models directly on edge devices, drones, and rugged tablets. Detections are automatically tagged with sub-meter GPS coordinates, localized onto GIS maps, and converted into prioritized maintenance action items.",
    },
  ],
  phases: {
    study: {
      focus:
        "Assess inspection workflows, imaging hardware (thermal, drone, multispectral), target defect classes, and connectivity constraints.",
      produces: [
        "Defect taxonomy and visual signature catalog",
        "Sensor, lens, and radiometric hardware specification",
        "Edge vs. cloud compute and synchronization requirements",
        "GIS and work-management system integration plan",
      ],
      fit: {
        good: [
          "Commercial agricultural operations and energy utilities with large-scale physical assets requiring regular visual audits",
          "Organizations with existing drone, thermal camera, or mobile scouting programs seeking automated triage",
        ],
        poor: [
          "Operations with poor quality or uncalibrated camera hardware producing inconsistent imagery",
          "Scenarios where visual evidence is insufficient to distinguish underlying electrical or biological root causes",
        ],
      },
    },
    "initial-design": {
      focus:
        "Curate and annotate domain training datasets, design model architectures, and establish detection benchmark metrics.",
      produces: [
        "Annotated ground-truth image dataset with boundary polygons",
        "Fine-tuned vision model benchmarks (mAP, precision, recall)",
        "Edge inference runtime specification and model quantization profile",
        "Geospatial visualization and diagnostic dashboard wireframes",
      ],
    },
    "detailed-design": {
      focus:
        "Build the image ingestion pipeline, inference microservices, GIS mapping overlays, and alert workflows.",
      produces: [
        "Production computer vision inference service with batch and stream processing",
        "Georeferenced map overlay module displaying defect heatmaps",
        "Automated work order generation adapter for CMMS and farm management backends",
        "Offline inspection caching and synchronization client",
      ],
    },
    implementation: {
      focus:
        "Deploy diagnostic vision pipelines, calibrate against field inspections, and train operational crews.",
      produces: [
        "Deployed edge and cloud diagnostic vision infrastructure",
        "Field inspection procedure and image capture guidelines",
        "Live detection accuracy tracking and model drift monitoring",
        "Operator and agronomist diagnostic review training",
      ],
    },
  },
};
